/**
 * Team Slide
 * Founder + AI CTO — responsive side-by-side layout
 */

import React from 'react';
import { HeadCircuitIcon } from '@phosphor-icons/react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { media } from '@config/breakpoints';
import {
  SlideBase,
  SlideHeader,
  SlideContent,
  SlideAccentGlow,
  PersonPhoto,
  PersonName,
  PersonTitle,
  PersonBio,
  CardRow,
} from '@components/press/pitch-deck/slide-primitives';

import linkedinLogo from '@components/press/pitch-deck/logos/linkedin_logo.jpeg';
import geLogo from '@components/press/pitch-deck/logos/ge_digital_logo.jpeg';
import stitchFixLogo from '@components/press/pitch-deck/logos/stitch_fix_logo.jpeg';
import pinterestLogo from '@components/press/pitch-deck/logos/pinterest_logo.jpeg';
import ucBerkeleyLogo from '@components/press/pitch-deck/logos/uc_berkeley_logo.jpeg';
import claudeLogo from '@components/press/pitch-deck/logos/claude-color.png';
import chatgptLogo from '@components/press/pitch-deck/logos/ChatGPT-Logo.png';
import geminiLogo from '@components/press/pitch-deck/logos/gemini_logo.png';

// ============================================
// Profile photo URL
// ============================================

const JUNE_PHOTO_URL = 'https://latlonglabs.com/img/june_bio_pic.png';

// ============================================
// Responsive Team Layout
// ============================================

const TeamContent = styled(SlideContent)`
  gap: ${spacing.lg};
  justify-content: center;
`;

const TeamRow = styled(CardRow)`
  align-items: flex-start;
  justify-content: center;

  ${media.md} {
    gap: ${spacing.xs};
  }
`;

const Card = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.xs};

  ${media.md} {
    gap: ${spacing.md};
    padding: ${spacing.sm};
  }
`;

// ── AI Logo Triangle ──

const LogoContainer = styled.div`
  width: clamp(104px, 13vw, 169px);
  height: clamp(104px, 13vw, 169px);
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${colors.highlight.extraDark},
    ${colors.brand.exxtraDark}
  );
  border: 3px solid ${colors.brand.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// ── Slide-local overrides ──

const BigPersonPhoto = styled(PersonPhoto)`
  width: clamp(104px, 13vw, 169px);
  height: clamp(104px, 13vw, 169px);
  background-position: center 20%;
  background-size: 140%;
`;

// ── 1.25× font sizes (shared primitives) ──

const BigPersonName = styled(PersonName)`
  font-size: clamp(calc(${typography.fontSize.base} * 1.25), 2.25vw, calc(${typography.fontSize.xl} * 1.25));
`;

const BigPersonTitle = styled(PersonTitle)`
  font-size: clamp(calc(${typography.fontSize.xs} * 1.25), 1.375vw, calc(${typography.fontSize.base} * 1.25));
`;

const BigPersonBio = styled(PersonBio)`
  font-size: clamp(calc(${typography.fontSize.xxs} * 1.25), 1.125vw, calc(${typography.fontSize.sm} * 1.25));
  text-align: left;
  max-width: none;
  width: 90%;

  p {
    margin: 0 0 0.75em;
  }

  ul {
    margin: 0.25em 0 0.75em;
    padding-left: 1.2em;
  }
`;

const Credential = styled.span`
  font-size: clamp(8.75px, 0.875vw, 12.5px);
  color: ${colors.highlight.dark};
  background: ${colors.brand.primaryLight};
  border: 1px solid ${colors.brand.primaryDark};
  border-radius: ${borderRadius.full};
  padding: 2px 8px;
  margin: 2px;
`;

const CredentialRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  width: 90%;
`;

const CompanyLogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
  padding: ${spacing.xs} 0;
  width: 90%;
`;

const CompanyLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const CompanyLogoImg = styled.img`
  height: clamp(20px, 2.5vw, 28px);
`;

// ============================================
// Hiring Roster (moved from Path to Victory)
// ============================================

const HiringRoster = styled.div`
  width: 100%;
  max-width: 960px;
  padding: ${spacing.lg} ${spacing.xl};
  background: rgba(16, 147, 132, 0.06);
  border: 1px solid rgba(16, 147, 132, 0.15);
  border-radius: ${borderRadius.xl};
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const RosterLabel = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: calc(${typography.fontSize.xs} * 1.25);
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.accent.teal.exxtraDark};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  text-align: center;
`;

const RosterRow = styled.div`
  display: flex;
  gap: ${spacing.lg};
  justify-content: center;
  flex-wrap: wrap;
`;

const RosterMember = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 80px;
`;

const RosterAvatar = styled.div<{ $bg?: string }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${props => props.$bg || colors.brand.primaryDark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(${typography.fontSize.base} * 1.25);
  color: ${colors.highlight.dark};
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

  ${media.md} {
    width: 52px;
    height: 52px;
  }
`;

const RosterMemberTitle = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: calc(${typography.fontSize.xxs} * 1.25);
  color: ${colors.highlight.primary};
  text-align: center;
  line-height: 1.3;

  ${media.md} {
    font-size: calc(${typography.fontSize.xs} * 1.25);
  }
`;

const RosterDivider = styled.div`
  width: 3px;
  height: 80px;
  background: rgba(51, 65, 85, 0.12);
  align-self: center;
`;


// ============================================
// AI Logo Triangle SVG
// ============================================

const AiLogoTriangle: React.FC = () => (
  <svg
    viewBox="0 0 130 120"
    width="60%"
    height="60%"
    style={{ overflow: 'visible' }}
  >
    <defs>
      <clipPath id="ai-clip-top"><circle cx="65" cy="18" r="32" /></clipPath>
      <clipPath id="ai-clip-bl"><circle cx="16" cy="88" r="32" /></clipPath>
      <clipPath id="ai-clip-br"><circle cx="114" cy="88" r="32" /></clipPath>
    </defs>
    {/* Claude — top */}
    <circle cx="65" cy="18" r="32" fill="white" opacity="0.15" />
    <image href={claudeLogo} x="33" y="-14" width="64" height="64" clipPath="url(#ai-clip-top)" />
    {/* Gemini — bottom left */}
    <circle cx="16" cy="88" r="32" fill="white" opacity="0.15" />
    <image href={geminiLogo} x="-16" y="56" width="64" height="64" clipPath="url(#ai-clip-bl)" />
    {/* ChatGPT — bottom right (light bg for visibility) */}
    <circle cx="114" cy="88" r="32" fill="white" opacity="0.45" />
    <image href={chatgptLogo} x="82" y="56" width="64" height="64" clipPath="url(#ai-clip-br)" />
  </svg>
);

// ============================================
// Component
// ============================================

export const TeamSlide: React.FC = () => (
  <SlideBase>
    <SlideAccentGlow />
    <TeamContent>
      <SlideHeader title="Team" />

      <TeamRow>
        {/* ── June Andrews ── */}
        <Card>
          <BigPersonPhoto $src={JUNE_PHOTO_URL} />
          <BigPersonName>Dr. June Andrews</BigPersonName>
          <BigPersonTitle>Founder</BigPersonTitle>
          <BigPersonBio>
            <p>History of building and leading teams for cutting edge ML products with world wide impact</p>
            <ul>
              <li>First real-time ML for GE&rsquo;s jet engine fleet, 60% of the world&rsquo;s commercial jet engines</li>
              <li>First platform for predicting performance of fashion, before production at Stitch Fix</li>
              <li>Data for Growth at LinkedIn 2013 - 2015, responsible for SignUps, MAU and Engagement</li>
              <li>Search for Pinterest and Yelp</li>
            </ul>
            <p>UC Berkeley lecturer in Applied ML.</p>
            <p>Mom of two in a loving dual career household. Well connected to the beachhead demographic.</p>
          </BigPersonBio>
          <CompanyLogoRow>
            <CompanyLogo><CompanyLogoImg src={linkedinLogo} alt="LinkedIn" /></CompanyLogo>
            <CompanyLogo><CompanyLogoImg src={geLogo} alt="GE Digital" /></CompanyLogo>
            <CompanyLogo><CompanyLogoImg src={stitchFixLogo} alt="Stitch Fix" /></CompanyLogo>
            <CompanyLogo><CompanyLogoImg src={pinterestLogo} alt="Pinterest" /></CompanyLogo>
            <CompanyLogo><CompanyLogoImg src={ucBerkeleyLogo} alt="UC Berkeley" /></CompanyLogo>
          </CompanyLogoRow>
          <CredentialRow>
            <Credential>PhD Cornell &middot; Applied Math (Social Networks)</Credential>
            <Credential>NSF Fellow</Credential>
          </CredentialRow>
        </Card>

        {/* ── AI Engineering Team ── */}
        <Card>
          <LogoContainer>
            <AiLogoTriangle />
          </LogoContainer>
          <BigPersonName>AI Engineering Team</BigPersonName>
          <BigPersonTitle>CTO: Claude, Gemini &amp; Codex</BigPersonTitle>
          <BigPersonBio>
            A rotation of Claude, Gemini and Codex serves for raw code, research, data mining, branding, marketing....everything is built in house on Google Cloud with these agents. Building is cheaper, more customized, sometimes faster and more reliably integrated than cobbling together vendors. Current spend is ~$300/month on tokens and infrastructure.
          </BigPersonBio>
          <CredentialRow>
            <Credential>Zero to App Store in 6 months</Credential>
            <Credential>AI-native from day one</Credential>
          </CredentialRow>
        </Card>
      </TeamRow>

      <HiringRoster>
        <RosterLabel>PreSeed Team</RosterLabel>
        <RosterRow>
          <RosterMember>
            <RosterAvatar $bg={colors.accent.teal.light}>JA</RosterAvatar>
            <RosterMemberTitle>CEO</RosterMemberTitle>
          </RosterMember>
          <RosterMember>
            <RosterAvatar $bg={colors.accent.blue.light}><HeadCircuitIcon size={24} weight="duotone" /></RosterAvatar>
            <RosterMemberTitle>AI</RosterMemberTitle>
          </RosterMember>
          <RosterDivider />
          <RosterMember>
            <RosterAvatar>BE</RosterAvatar>
            <RosterMemberTitle>Backend<br />Engineer</RosterMemberTitle>
          </RosterMember>
          <RosterMember>
            <RosterAvatar $bg={colors.brand.primary}>AI</RosterAvatar>
            <RosterMemberTitle>AI App<br />Engineer</RosterMemberTitle>
          </RosterMember>
          <RosterMember>
            <RosterAvatar $bg={colors.accent.blue.extraLight}>CM</RosterAvatar>
            <RosterMemberTitle>Community</RosterMemberTitle>
          </RosterMember>
          <RosterMember>
            <RosterAvatar>FE</RosterAvatar>
            <RosterMemberTitle>Frontend<br />Engineer</RosterMemberTitle>
          </RosterMember>
          <RosterDivider />
          <RosterMember>
            <RosterAvatar $bg={colors.brand.extraLight}>+</RosterAvatar>
            <RosterMemberTitle>Back Office<br />Contractors</RosterMemberTitle>
          </RosterMember>
        </RosterRow>
      </HiringRoster>
    </TeamContent>
  </SlideBase>
);
